import { v4 as uuidv4 } from "uuid";
import firebase from "firebase/compat/app";
import { firestore } from "../firebaseConfig";
import {
  IOrder,
  IContactPerson,
  IRouteItem,
  ICustomer,
  IOrderProduct,
  IInvoice,
} from "../types";
import { query, doc } from "firebase/firestore";
import { OrderStatus } from "../types/order/IOrder";
import { IOrderValidated } from "../types/order/IOrder";
import { CreateOrderStepsEnum } from "../pages/orders/create-order/CreateOrderStepsEnum";
import {
  validateMobile,
  validateStringLength,
} from "../helpers/validationHelper";
import { IInvoiceItem } from "../types/Invoice";
import { ICompany } from "../types/CompanyPerson";
import { IPaymentMeans, IInvoiceAmount } from "../types/Invoice";

type DocumentSnapshot = firebase.firestore.DocumentSnapshot;
type SnapshotOptions = firebase.firestore.SnapshotOptions;

class Invoice implements IInvoice {
  id: string = "";
  workspaceId: string = "";
  createdAt: string = "";
  lastUpdated: string = "";
  createdBy: string = "";
  invoiceNumber: string = "";
  invoicer?: ICompany;
  invoicee?: ICustomer;
  isBooked: boolean = false;
  isSent: boolean = false;
  isPaid: boolean = false;
  isCredited: boolean = false;
  isCreditInvoice: boolean = false;
  remainingBalance: number = 0;
  bookedAt?: string;
  sentAt?: string;
  customerSentAt?: string;
  accountingSentAt?: string;
  lastCustomerRecipientEmail?: string;
  lastAccountingRecipientEmail?: string;
  deliveryHistory?: IInvoice["deliveryHistory"];
  lifecycleStatus?: IInvoice["lifecycleStatus"];
  discardedAt?: string;
  discardedBy?: string;
  finalizedAt?: string;
  finalizedBy?: string;
  issuanceMode?: IInvoice["issuanceMode"];
  accountingState?: IInvoice["accountingState"];
  accountingMode?: IInvoice["accountingMode"];
  accountingProviderKey?: string;
  accountingConfirmedAt?: string;
  accountingExternalInvoiceNumber?: string;
  accountingSettlementStatus?: IInvoice["accountingSettlementStatus"];
  accountingAmountOutstanding?: string;
  accountingLastSyncedAt?: string;
  accountingLastErrorCode?: string;
  paidAt?: string;
  creditedAt?: string;
  paidAmount: number = 0;
  creditedAmount: number = 0;
  relatedOrderId: string = "";
  relatedOrderIds?: string[];
  sourceOrders?: IInvoice["sourceOrders"];
  paymentMeans?: IPaymentMeans;
  invoiceDate: string = "";
  dueDate: string = "";
  companyId: string = "";
  customerId: string = "";
  items: IInvoiceItem[] = [];
  amounts?: IInvoiceAmount;
  yourReference?: string;
  ourReference?: string;

  static collectionName = "orders";

  static converter = {
    toFirestore(order: Invoice) {
      return order.data();
    },
    fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions) {
      let data = snapshot.data(options) as IInvoice;
      return new Invoice({
        ...data,
        id: snapshot.id,
      });
    },
  };

  static createId() {
    return firestore.collection(Invoice.collectionName).doc().id;
  }

  constructor(data: Partial<IInvoice> = {}) {
    Object.assign(this, data);
  }

  clone() {
    return new Invoice({ ...this });
  }

  setData(updates: Partial<IInvoice>) {
    Object.assign(this, updates);
  }

  toPlainObj(): IInvoice {
    return { ...this };
  }

  data(): IInvoice {
    return this.toPlainObj();
  }

  static default = (): IInvoice => {
    return {
      id: "",
      workspaceId: "",
      createdAt: "",
      lastUpdated: "",
      createdBy: "",
      invoiceNumber: "",
      invoicer: undefined,
      invoicee: undefined,
      isBooked: false,
      lifecycleStatus: "draft",
      issuanceMode: undefined,
      isSent: false,
      isPaid: false,
      isCredited: false,
      isCreditInvoice: false,
      remainingBalance: 0,
      bookedAt: undefined,
      sentAt: undefined,
      paidAt: undefined,
      creditedAt: undefined,
      paidAmount: 0,
      creditedAmount: 0,
      relatedOrderId: "",
      paymentMeans: undefined,
      invoiceDate: "",
      dueDate: "",
      companyId: "",
      customerId: "",
      items: [],
      amounts: undefined,
    };
  };

  static get = (id: string) => {
    return doc(
      firestore
        .collection(Invoice.collectionName)
        .withConverter(Invoice.converter),
      id,
    );
  };

  static list = (workspaceId: string, statuses: OrderStatus[] = []) => {
    let request = firestore
      .collection(Invoice.collectionName)
      .where("workspaceId", "==", workspaceId);

    if (statuses.length > 0) {
      request = request.where("status", "in", statuses);
    }

    return query(request.withConverter(Invoice.converter));
  };

  static validateCustomer = (customer: ICustomer) => {
    return customer && customer.id !== "";
  };

  static validateRoute = (routes: IRouteItem[]) => {
    let valid = true;
    for (const route of routes) {
      if (
        !(
          route.stopDate !== undefined &&
          route.location &&
          validateStringLength(route.location.addressLine ?? "", 2) &&
          route.location.postCode?.length === 4 &&
          validateStringLength(route.location.city, 2)
        )
      ) {
        valid = false;
      }
    }
    return valid;
  };

  static validateCargo = (cargo: IOrderProduct[]) => {
    let valid = true;
    for (const item of cargo) {
      if (
        !(
          item.itinerary !== undefined &&
          item.itinerary?.pickupStopNumber <
            item.itinerary?.deliveryStopNumber &&
          item.quantity > 0 &&
          item.descriptionShort.length &&
          item.height > 0 &&
          item.weight > 0 &&
          item.length > 0 &&
          item.weight > 0 &&
          item.pricing &&
          item.pricing.priceModel &&
          item.pricing.priceModel.length > 0 &&
          item.productType
        )
      ) {
        valid = false;
      }
    }
    return cargo.length ? valid : false;
  };

  static validateContacts = (contacts: IContactPerson[]) => {
    let valid = true;
    for (const person of contacts) {
      if (
        !(
          person.location &&
          validateStringLength(person.name, 3) &&
          validateMobile(person.phoneNumber)
        )
      ) {
        valid = false;
      }
    }
    return contacts.length ? valid : false;
  };

  static validateDriver = (driverId: string | undefined) => {
    return driverId !== undefined && driverId !== "";
  };

  static validateStep = (order: IOrder, step: number) => {
    let validation = false;
    switch (step) {
      case CreateOrderStepsEnum.CUSTOMER_SELECTION:
        validation = Invoice.validateCustomer(order.customer);
        break;
      case CreateOrderStepsEnum.ROUTE_SELECTION:
        validation = Invoice.validateRoute(order.route);
        break;
      case CreateOrderStepsEnum.PRODUCT_MANAGEMENT:
        validation = Invoice.validateCargo(order.cargo);
        break;
      case CreateOrderStepsEnum.DRIVER_SELECTION:
        validation = Invoice.validateDriver(order.driverId);
        break;
      default:
        validation = false;
        break;
    }
    return validation;
  };

  static validateOrder(order: IOrder) {
    let validationValues = {
      customer:
        order.customerId &&
        order.customerId !== "" &&
        order.customer &&
        order.customer.id !== "",
      route: order.route && order.route.length > 0,
      cargo: order.cargo && order.cargo.length > 0,
    } as IOrderValidated;

    let invalid = Object.entries(validationValues);
    return {
      isValidOrder: invalid.filter(([key, value]) => !value).length === 0,
      invalidFields: Object.fromEntries(invalid),
    };
  }

  static create = async (data: Omit<IInvoice, "id" | "createdAt">) => {
    const newInvoice = new Invoice({
      ...data,
      id: uuidv4().toString(),
      createdAt: new Date().toISOString(),
    });
    try {
      await firestore
        .collection(Invoice.collectionName)
        .doc(newInvoice.id)
        .withConverter(Invoice.converter)
        .set(newInvoice, { merge: true });
    } catch (e) {
      console.warn("Create invoice failed with error: ", e);
      return false;
    }
    return true;
  };

  static remove = async (invoice: Invoice) => {
    return await firestore
      .collection(Invoice.collectionName)
      .doc(invoice.id)
      .delete();
  };

  static update = async (invoice: Invoice, updates: Partial<IInvoice>) => {
    invoice.setData(updates);
    try {
      await firestore
        .collection(Invoice.collectionName)
        .doc(invoice.id)
        .withConverter(Invoice.converter)
        .set(invoice, { merge: true });
    } catch (e) {
      console.warn(e);
    }
    return invoice;
  };
}

export default Invoice;
